/** Pure comparison of BMP-managed QBO sales lines. Unchanged manual lines are not shown. */
export type InvoiceLineForComparison = {
  itemId?: string | null;
  itemName?: string | null;
  description?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  amountCents?: number | null;
};
export type BmpInvoiceLineForComparison = {
  quickBooksItemId: string;
  description: string;
  quantity: number;
  unitPrice: number;
};
export type InvoiceLineChange = {
  key: string;
  title: string;
  previous: InvoiceLineForComparison | null;
  proposed: InvoiceLineForComparison | null;
};

export function isBmpManagedLine(description?: string | null): boolean {
  const text = (description ?? '').trim();
  return (
    /BMP quantity\s*:/i.test(text) ||
    /^BMP\s+(?:per-ton delivery fee|direct ship fee)/i.test(text) ||
    /^Fuel surcharge\s*(?:\(|$)/i.test(text)
  );
}

function kind(description?: string | null): string {
  const text = description ?? '';
  if (/BMP quantity\s*:/i.test(text)) return 'product';
  if (/^BMP\s+per-ton delivery fee/i.test(text)) return 'delivery';
  if (/^BMP\s+direct ship fee/i.test(text)) return 'direct-ship';
  if (/^Fuel surcharge/i.test(text)) {
    const subtype =
      /\((?:material|delivery|delivered price)[^)]*\)/i.exec(text)?.[0].toLowerCase() ?? 'general';
    return `fuel:${subtype}`;
  }
  return 'other';
}
function load(description?: string | null): string {
  return /\bLoad\s*:?\s*([A-Za-z0-9_-]+)/i.exec(description ?? '')?.[1] ?? '';
}
const normalized = (x?: string | null) => (x ?? '').replace(/\s+/g, ' ').trim();
/** QBO Ship Via is limited to 31 characters. */
function normalizedQboShipVia(value?: string | null): string {
  return (value ?? '').trim().slice(0, 31).trimEnd();
}

const inCents = (n?: number | null) => (n == null ? null : Math.round(n * 100));

function changeTitle(row: InvoiceLineForComparison): string {
  const category = kind(row.description);
  if (category === 'product') return row.itemName || 'Material';
  if (category === 'delivery') return row.itemName || 'Per-ton delivery';
  if (category === 'direct-ship') return row.itemName || 'Direct ship fee';
  if (category.startsWith('fuel:')) return row.description || 'Fuel surcharge';
  return row.itemName || row.description || 'QuickBooks line';
}

/** Reconcile by line category + QBO service item + load; repeated keys use ordinal occurrence. */
export function diffBmpInvoiceLines(
  oldLines: readonly InvoiceLineForComparison[],
  newLines: readonly BmpInvoiceLineForComparison[]
): InvoiceLineChange[] {
  const oldOwned = oldLines.filter((line) => isBmpManagedLine(line.description));
  const newOwned = newLines.filter((line) => isBmpManagedLine(line.description));
  const bucket = (rows: readonly InvoiceLineForComparison[]) => {
    const counts = new Map<string, number>();
    return rows.map((row) => {
      const base = [kind(row.description), row.itemId ?? '', load(row.description)].join('|');
      const occurrence = counts.get(base) ?? 0;
      counts.set(base, occurrence + 1);
      return { key: `${base}|${occurrence}`, row };
    });
  };
  const oldIndex = new Map(bucket(oldOwned).map(({ key, row }) => [key, row]));
  const revised = newOwned.map((row) => ({
    ...row,
    itemId: row.quickBooksItemId,
    amountCents: Math.round((row.quantity ?? 0) * (row.unitPrice ?? 0) * 100),
  }));
  const result: InvoiceLineChange[] = [];
  for (const { key, row } of bucket(revised)) {
    const previous = oldIndex.get(key) ?? null;
    oldIndex.delete(key);
    const changed =
      !previous ||
      previous.amountCents !== row.amountCents ||
      inCents(previous.unitPrice) !== inCents(row.unitPrice) ||
      previous.quantity !== row.quantity ||
      normalized(previous.description) !== normalized(row.description);
    if (changed) result.push({ key, title: changeTitle(row), previous, proposed: row });
  }
  for (const [key, previous] of oldIndex) {
    result.push({ key, title: changeTitle(previous), previous, proposed: null });
  }
  return result;
}

export type HeaderChange = { key: string; label: string; before: string; after: string };
export function diffOrderOwnedHeaders(
  current: Record<string, string | null | undefined>,
  proposed: Partial<Record<string, string | null | undefined>>,
  labels: Record<string, string>
): HeaderChange[] {
  return Object.entries(labels)
    .filter(
      ([key]) =>
        Object.prototype.hasOwnProperty.call(proposed, key) &&
        (key === 'shipVia'
          ? normalizedQboShipVia(current[key]) !== normalizedQboShipVia(proposed[key])
          : normalized(current[key]) !== normalized(proposed[key]))
    )
    .map(([key, label]) => ({
      key,
      label,
      before: current[key] ?? '',
      after: proposed[key] ?? '',
    }));
}
